import { getTestDate } from './reportFilterUtils';
import { formatTry } from './reportRevenueUtils';

export const PORTFOLIO_DECISION_LABELS = {
  pending: 'Beklemede',
  apply: 'Uygula',
  reject: 'Reddet',
  retest: 'Tekrar Test',
};

/** Filter tests by brand ids and optional date range */
const filterTests = (brands, { brandIds = [], dateFrom = '', dateTo = '' }) => {
  const selectedBrands =
    brandIds.length > 0 ? brands.filter((b) => brandIds.includes(String(b._id))) : brands;

  const rows = [];

  selectedBrands.forEach((brand) => {
    (brand.tests || []).forEach((test) => {
      const testDate = getTestDate(test);
      if (dateFrom && testDate) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (testDate < from) return;
      }
      if (dateTo && testDate) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (testDate > to) return;
      }

      rows.push({
        ...test,
        brandName: brand.name,
        brandId: brand._id,
      });
    });
  });

  return rows.sort((a, b) => (getTestDate(b)?.getTime() || 0) - (getTestDate(a)?.getTime() || 0));
};

/** Build portfolio summary from grouped brand reports */
export const buildPortfolioReport = (brands, options = {}) => {
  const tests = filterTests(brands, options);
  const winners = tests.filter(
    (t) => t.hasSignificant && (t.resultStatuses || []).includes('Kazandı'),
  );

  const upliftValues = winners
    .map((t) => t.maxImprovement)
    .filter((v) => typeof v === 'number' && !Number.isNaN(v));

  const avgUplift = upliftValues.length
    ? Math.round((upliftValues.reduce((a, b) => a + b, 0) / upliftValues.length) * 10) / 10
    : 0;

  const totalRevenue = winners.reduce((sum, t) => sum + (t.estimatedYearlyRevenue || 0), 0);

  const decisionCounts = {};
  tests.forEach((t) => {
    const key = t.decision || 'pending';
    decisionCounts[key] = (decisionCounts[key] || 0) + 1;
  });

  const selectedBrandNames =
    options.brandIds?.length > 0
      ? brands.filter((b) => options.brandIds.includes(String(b._id))).map((b) => b.name)
      : brands.map((b) => b.name);

  return {
    generatedAt: new Date(),
    dateFrom: options.dateFrom || '',
    dateTo: options.dateTo || '',
    brandNames: selectedBrandNames,
    summary: {
      totalTests: tests.length,
      winnerCount: winners.length,
      winRate: tests.length ? Math.round((winners.length / tests.length) * 100) : 0,
      avgUplift,
      totalRevenue,
    },
    winners,
    tests,
    decisionCounts,
  };
};

/** Download portfolio as standalone HTML file */
export const downloadPortfolioHtml = (report) => {
  const { summary, winners, decisionCounts, brandNames, dateFrom, dateTo, generatedAt } = report;

  const decisionRows = Object.entries(decisionCounts)
    .map(
      ([key, count]) =>
        `<tr><td>${PORTFOLIO_DECISION_LABELS[key] || key}</td><td>${count}</td></tr>`,
    )
    .join('');

  const winnerRows = winners
    .map(
      (t) => `
      <tr>
        <td>${t.brandName}</td>
        <td>${t.name}</td>
        <td>+${t.maxImprovement ?? '—'}%</td>
        <td>${PORTFOLIO_DECISION_LABELS[t.decision] || '—'}</td>
        <td>${t.estimatedYearlyRevenue ? formatTry(t.estimatedYearlyRevenue) : '—'}</td>
      </tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8"/>
  <title>HypeCRO Portföy Raporu</title>
  <style>
    body { font-family: Arial, sans-serif; color: #333; padding: 32px; max-width: 960px; margin: 0 auto; }
    h1 { color: #240B4B; margin-bottom: 4px; }
    .meta { color: #666; margin-bottom: 24px; font-size: 14px; }
    .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
    .kpi { background: #f7f9fc; border-radius: 8px; padding: 16px; }
    .kpi strong { display: block; font-size: 24px; color: #240B4B; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 14px; }
    th, td { border: 1px solid #eee; padding: 10px; text-align: left; }
    th { background: #240B4B; color: #fff; }
    h2 { color: #240B4B; font-size: 18px; margin-top: 32px; }
  </style>
</head>
<body>
  <h1>HypeCRO Portföy Raporu</h1>
  <p class="meta">
    Markalar: ${brandNames.join(', ') || 'Tümü'}<br/>
    Dönem: ${dateFrom || '—'} — ${dateTo || '—'}<br/>
    Oluşturulma: ${generatedAt.toLocaleString('tr-TR')}
  </p>
  <div class="kpis">
    <div class="kpi"><strong>${summary.totalTests}</strong>Toplam Test</div>
    <div class="kpi"><strong>${summary.winnerCount}</strong>Kazanan Test</div>
    <div class="kpi"><strong>+${summary.avgUplift}%</strong>Ort. Uplift</div>
    <div class="kpi"><strong>${formatTry(summary.totalRevenue)}</strong>Yıllık Gelir Etkisi</div>
  </div>
  <h2>Karar Dağılımı</h2>
  <table><thead><tr><th>Karar</th><th>Adet</th></tr></thead><tbody>${decisionRows || '<tr><td colspan="2">Veri yok</td></tr>'}</tbody></table>
  <h2>Kazanan Testler</h2>
  <table>
    <thead><tr><th>Marka</th><th>Test</th><th>Uplift</th><th>Karar</th><th>Gelir / Yıl</th></tr></thead>
    <tbody>${winnerRows || '<tr><td colspan="5">Kazanan test bulunamadı</td></tr>'}</tbody>
  </table>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `hypecro-portfolio-${Date.now()}.html`;
  link.click();
  URL.revokeObjectURL(url);
};

/** Export preview element as PDF via html2canvas + jspdf */
export const downloadPortfolioPdf = async (element) => {
  if (!element) return;

  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');

  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(`hypecro-portfolio-${Date.now()}.pdf`);
};
